import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker, Input } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

export function RoomBookingsFilter() {
  const nav = useNavigate({ from: "/app/room-bookings" });
  const search = useSearch({
    from: "/app/room-bookings",
  }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const dateFromFilter = findCondition(filters, "date");
  const statusFilter = findCondition(filters, "status");
  const contactNameFilter = findCondition(filters, "contactName");
  const titleFilter = findCondition(filters, "title");

  const [dateValue, setDateValue] = useState<string>(
    (dateFromFilter?.value && typeof dateFromFilter.value === "string"
      ? dateFromFilter.value
      : "") || "",
  );
  const [contactNameValue, setContactNameValue] = useState<string>(
    (contactNameFilter?.value as string) || "",
  );
  const [titleValue, setTitleValue] = useState<string>(
    (titleFilter?.value as string) || "",
  );

  useEffect(() => {
    setDateValue(
      dateFromFilter?.value && typeof dateFromFilter.value === "string"
        ? dateFromFilter.value
        : "",
    );
  }, [dateFromFilter?.value]);
  useEffect(
    () => setContactNameValue((contactNameFilter?.value as string) || ""),
    [contactNameFilter?.value],
  );
  useEffect(
    () => setTitleValue((titleFilter?.value as string) || ""),
    [titleFilter?.value],
  );

  const statusValue =
    typeof statusFilter?.value === "string" ? statusFilter.value : "";

  const hasFilter =
    !!dateFromFilter?.value ||
    !!statusValue ||
    !!contactNameFilter?.value ||
    !!titleFilter?.value;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <DatePicker
            value={dateValue ? new Date(dateValue) : undefined}
            onChange={(date) => {
              const dateStr = date ? format(date, "yyyy-MM-dd") : "";
              setDateValue(dateStr);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (dateStr) {
                nextFilters = upsertCondition(filters, {
                  field: "date",
                  op: "eq",
                  value: dateStr,
                });
              } else {
                nextFilters = removeConditions(filters, "date");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            className="h-8 sm:max-w-2xs"
          />
          <Input
            placeholder="ຄົ້ນຫາຊື່ຜູ້ຈອງ..."
            value={contactNameValue}
            onChange={(e) => {
              const val = e.target.value;
              setContactNameValue(val);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "contactName",
                  op: "contains",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "contactName");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            className="h-8 sm:max-w-2xs"
          />
          <Input
            placeholder="ຄົ້ນຫາຊື່ / ວັດຖຸປະສົງ..."
            value={titleValue}
            onChange={(e) => {
              const val = e.target.value;
              setTitleValue(val);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "title",
                  op: "contains",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "title");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            className="h-8 sm:max-w-2xs"
          />
          <SimpleSelect
            value={statusValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "scheduled", label: "ຈອງແລ້ວ" },
              { value: "completed", label: "ສຳເລັດ" },
              { value: "cancelled", label: "ຍົກເລີກ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
